import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { StatCard } from "@/components/common/StatCard";
import { ActivityItem } from "@/components/common/ActivityItem";
import { Button } from "@/components/ui/button";
import { statusMeta, STATUS_ORDER } from "@/lib/constants";
import { formatINRShort } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  Users, UserCheck, Activity, Car, CheckCircle2, Trophy, FileText, FileCheck2,
  IndianRupee, ScrollText, Store, User, ChevronRight, BarChart3, ArrowRight,
} from "lucide-react";

const LO_KEY = {
  NEW: "new", CONTACTED: "contacted", FOLLOW_UP: "followup", NEGOTIATION: "negotiation",
  OFFER: "offer", DEAL_DONE: "deal_done", LOST: "lost",
};

export default function TeamDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/team").then((res) => setData(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader label="Loading team overview…" />;
  if (!data) return null;

  const o = data.team_overview;
  const lo = data.lead_overview;
  const po = data.processing_overview;
  const maxLo = Math.max(1, ...STATUS_ORDER.map((s) => lo[LO_KEY[s]] || 0));

  return (
    <div className="animate-fade-in-up space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Team Leader</p>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Overview</h1>
        </div>
        <Button variant="outline" onClick={() => navigate("/reports")}>
          <BarChart3 className="h-4 w-4" /> <span className="hidden sm:inline">Reports</span>
        </Button>
      </div>

      {/* Team overview */}
      <section>
        <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Team</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Employees" value={o.total_employees} icon={Users} tone="brand" onClick={() => navigate("/team/employees")} />
          <StatCard label="Active" value={o.active_employees} icon={UserCheck} tone="success" onClick={() => navigate("/team/employees")} />
          <StatCard label="Today's Activity" value={o.today_activities} icon={Activity} />
          <StatCard label="Total Leads" value={o.total_leads} icon={Car} onClick={() => navigate("/leads")} />
          <StatCard label="Deals Done" value={o.total_deals_done} icon={CheckCircle2} tone="success" onClick={() => navigate("/leads?status=DEAL_DONE")} />
          <StatCard label="Completed" value={o.total_completed} icon={Trophy} tone="success" onClick={() => navigate("/leads?deal=completed")} />
        </div>
      </section>

      {/* Lead overview */}
      <section className="grid gap-4 lg:grid-cols-5">
        <div className="rounded-xl border border-border bg-card p-4 shadow-card lg:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Pipeline distribution</h2>
            <div className="flex gap-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Store className="h-3 w-3" /> {lo.dealership_leads} dealer</span>
              <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {lo.individual_leads} individual</span>
            </div>
          </div>
          <div className="space-y-2">
            {STATUS_ORDER.map((s) => {
              const meta = statusMeta(s);
              const count = lo[LO_KEY[s]] || 0;
              return (
                <button key={s} onClick={() => navigate(`/leads?status=${s}`)}
                  className="flex w-full items-center gap-3 rounded-lg py-1 text-left transition-colors hover:bg-secondary/50">
                  <span className="w-24 shrink-0 text-xs font-medium text-muted-foreground">{meta.label}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div className={cn("h-full rounded-full", meta.bar)} style={{ width: `${(count / maxLo) * 100}%` }} />
                  </div>
                  <span className="tnum w-6 shrink-0 text-right text-sm font-bold text-foreground">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Processing */}
        <div className="lg:col-span-2">
          <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Processing pending</h2>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Documents" value={po.documents_pending} icon={FileText} tone="warning" onClick={() => navigate("/leads?deal=documents_pending")} />
            <StatCard label="Approval" value={po.file_approval_pending} icon={FileCheck2} tone="warning" onClick={() => navigate("/leads?deal=approval_pending")} />
            <StatCard label="Payment" value={po.payment_pending} icon={IndianRupee} tone="danger" onClick={() => navigate("/leads?deal=payment_pending")} />
            <StatCard label="COD" value={po.cod_pending} icon={ScrollText} onClick={() => navigate("/leads?deal=cod_pending")} />
          </div>
        </div>
      </section>

      {/* Performance */}
      <section>
        <div className="mb-2.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground">Employee performance · this month</h2>
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => navigate("/team/employees")}>Manage team</Button>
        </div>
        <div className="space-y-2.5">
          {data.performance.map((p, i) => (
            <button key={p.employee_id} onClick={() => navigate(`/team/employees/${p.employee_id}`)}
              className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3.5 text-left shadow-card transition-shadow hover:shadow-elevated">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {i === 0 ? <Trophy className="h-4 w-4" /> : p.employee_name.slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{p.employee_name}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                  <span>{p.leads_created} leads</span>
                  <span>{p.deals_done} deals</span>
                  <span className="text-brand">{p.conversion}% conv.</span>
                  <span>{formatINRShort(p.business_amount)}</span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      </section>

      {/* Recent activity */}
      <section>
        <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Recent team activity</h2>
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
          {data.recent_activities.map((a) => <ActivityItem key={a.id} activity={a} />)}
        </div>
      </section>
    </div>
  );
}
